// PropostaComercialDocument.tsx
import React from 'react';
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    Image
} from '@react-pdf/renderer';

// Estilos do documento - Versão ultra-estabilizada para evitar loops
const styles = StyleSheet.create({
    // Estilo da página - Margem inferior aumentada para evitar conflito com rodapé
    page: {
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        paddingTop: 30,
        paddingLeft: 30,
        paddingRight: 30,
        paddingBottom: 60, // Aumentado para 60 para segurança total
        fontFamily: 'Helvetica',
        fontSize: 8,
        color: '#333333',
    },

    // Cabeçalho mais compacto
    headerBox: {
        flexDirection: 'row',
        marginBottom: 12,
        borderBottom: '0.5px solid #e0e0e0',
        paddingBottom: 12,
    },

    // Área da logo mais compacta
    logo: {
        width: '25%',
        height: 60,
    },

    // Imagem da logo
    logoImage: {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
    },

    // Informações da empresa
    companyInfo: {
        width: '75%',
        paddingLeft: 15,
    },

    // Nome da empresa
    companyName: {
        fontSize: 12,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 4,
    },

    // Informações gerais da empresa - duas colunas
    companyData: {
        fontSize: 7,
        marginBottom: 2,
        color: '#555555',
    },

    // Container de informações em duas colunas
    infoColumns: {
        flexDirection: 'row',
        marginTop: 2,
    },

    // Coluna de informações
    infoColumn: {
        width: '50%',
    },

    // Espaçador pequeno
    spacer: {
        height: 8,
    },

    // Seções compactas
    section: {
        marginTop: 8,
        marginBottom: 8,
        padding: 6,
        backgroundColor: '#f5f5f5',
        borderLeft: '3px solid #cccccc',
    },

    // Texto do título
    sectionTitle: {
        fontSize: 9,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 0,
    },

    // Texto do cliente
    clientText: {
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 3,
    },

    // Tabela simplificada - Removendo bordas verticais externas para estabilidade
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#f5f5f5',
        borderTopWidth: 0.5,
        borderBottomWidth: 0.5,
        borderColor: '#e0e0e0',
        marginTop: 4,
    },

    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: '#e0e0e0',
        minHeight: 20,
    },

    tableCell: {
        padding: 5,
        fontSize: 7.5,
        borderLeftWidth: 0.5,
        borderLeftColor: '#e0e0e0',
    },

    // Células específicas
    cellItem: {
        width: '8%',
    },

    cellDesc: {
        width: '47%',
    },

    cellQty: {
        width: '10%',
    },

    cellUnitValue: {
        width: '17.5%',
    },

    cellTotal: {
        width: '17.5%',
        borderRightWidth: 0.5,
        borderRightColor: '#e0e0e0',
    },

    // Estilo para o nome do produto (negrito)
    produtoNome: {
        fontFamily: 'Helvetica-Bold',
        fontSize: 7.5,
    },

    // Estilo para a descrição específica - Margem superior
    produtoDescricao: {
        fontSize: 7,
        lineHeight: 1.2,
        marginTop: 6, 
    },

    // Texto legal mais compacto
    legalText: {
        marginTop: 8,
        marginBottom: 16,
        fontSize: 6.5,
        lineHeight: 1.3,
        color: '#666666',
        textAlign: 'justify',
        padding: 6,
        borderWidth: 0.5,
        borderColor: '#e0e0e0',
        backgroundColor: '#f9f9f9',
    },

    // Assinatura compacta
    signatureArea: {
        marginTop: 20,
        alignItems: 'center',
    },

    signatureLine: {
        borderTopWidth: 0.75,
        borderTopColor: '#000',
        width: '40%',
        marginBottom: 3,
    },

    signatureText: {
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
        textAlign: 'center',
    },

    // Rodapé mais discreto
    footer: {
        position: 'absolute',
        bottom: 20,
        left: 30,
        right: 30,
        textAlign: 'center',
        fontSize: 6.5,
        color: '#999999',
        paddingTop: 6,
        borderTopWidth: 0.5,
        borderTopColor: '#e0e0e0',
    },
});

// Tipagem da proposta
interface PropostaData {
    empresa: string;
    cnpj: string;
    ie: string;
    endereco: string;
    cidade: string;
    assinatura: string;
    estado: string;
    cep: string;
    telefone: string;
    email: string;
    logoUrl: string;
    destinatario: string;
    pregao: string;
    processo: string;
    itens: Array<{
        item: string;
        nomeProduto: string;
        descricaoEspecifica: string;
        quantidade: string;
        valorUnitario: string;
        valorTotal: string;
    }>;
    valorTotal: string;
    validadeProposta: string;
    prazoEntrega: string;
    dadosBancarios: string;
    dataEmissao: string;
}

// Componente do documento
export const PropostaComercialDocument: React.FC<{ data: PropostaData }> = ({ data }) => (
    <Document>
        <Page size="A4" style={styles.page}>
            {/* Cabeçalho com logo e informações da empresa */}
            <View style={styles.headerBox}>
                <View style={styles.logo}>
                    <Image
                        src={data.logoUrl}
                        style={styles.logoImage}
                    />
                </View>
                <View style={styles.companyInfo}>
                    <Text style={styles.companyName}>{data.empresa}</Text>

                    <View style={styles.infoColumns}>
                        <View style={styles.infoColumn}>
                            <Text style={styles.companyData}>CNPJ: {data.cnpj}</Text>
                            <Text style={styles.companyData}>Endereço: {data.endereco}</Text>
                            <Text style={styles.companyData}>Tel.: {data.telefone}</Text>
                        </View>
                        <View style={styles.infoColumn}>
                            <Text style={styles.companyData}>I.E: {data.ie}</Text>
                            <Text style={styles.companyData}>{data.cidade} - {data.estado}</Text>
                            <Text style={styles.companyData}>E-mail: {data.email.toLowerCase()}</Text>
                        </View>
                    </View>
                </View>
            </View>

            {/* Seção "PROPOSTA COMERCIAL" */}
            <View style={styles.section}>
                <Text style={styles.sectionTitle}>PROPOSTA COMERCIAL</Text>
            </View>

            {/* Informações do cliente, pregão e processo */}
            <View style={[styles.section, { marginTop: 6 }]}>
                <Text style={styles.clientText}>{data.destinatario}</Text>
                <Text style={styles.clientText}>{data.pregao}</Text>
                <Text style={styles.clientText}>{data.processo}</Text>
            </View>

            {/* Tabela de produtos - Layout Plano e Ultra-Estável */}
            {/* Cabeçalho da tabela */}
            <View style={styles.tableHeader} wrap={false}>
                <View style={[styles.tableCell, styles.cellItem]}>
                    <Text style={{ fontFamily: 'Helvetica-Bold', textAlign: 'center' }}>ITEM</Text>
                </View>
                <View style={[styles.tableCell, styles.cellDesc]}>
                    <Text style={{ fontFamily: 'Helvetica-Bold' }}>DESCRIÇÃO</Text>
                </View>
                <View style={[styles.tableCell, styles.cellQty]}>
                    <Text style={{ fontFamily: 'Helvetica-Bold', textAlign: 'center' }}>QTDE</Text>
                </View>
                <View style={[styles.tableCell, styles.cellUnitValue]}>
                    <Text style={{ fontFamily: 'Helvetica-Bold', textAlign: 'right' }}>VALOR UNIT.</Text>
                </View>
                <View style={[styles.tableCell, styles.cellTotal]}>
                    <Text style={{ fontFamily: 'Helvetica-Bold', textAlign: 'right' }}>VALOR TOTAL</Text>
                </View>
            </View>

            {/* Linhas dos produtos */}
            {data.itens.map((item, index) => (
                <View key={index} style={styles.tableRow} wrap={false}>
                    <View style={[styles.tableCell, styles.cellItem]}>
                        <Text style={{ textAlign: 'center' }}>{item.item}</Text>
                    </View>
                    <View style={[styles.tableCell, styles.cellDesc]}>
                        <Text style={styles.produtoNome}>{item.nomeProduto}</Text>
                        {item.descricaoEspecifica && (
                            <Text style={styles.produtoDescricao}>{item.descricaoEspecifica}</Text>
                        )}
                    </View>
                    <View style={[styles.tableCell, styles.cellQty]}>
                        <Text style={{ textAlign: 'center' }}>{item.quantidade}</Text>
                    </View>
                    <View style={[styles.tableCell, styles.cellUnitValue]}>
                        <Text style={{ textAlign: 'right' }}>R$ {item.valorUnitario.replace(/^R\$\s?/, '')}</Text>
                    </View>
                    <View style={[styles.tableCell, styles.cellTotal]}>
                        <Text style={{ textAlign: 'right' }}>R$ {item.valorTotal.replace(/^R\$\s?/, '')}</Text>
                    </View>
                </View>
            ))}

            {/* Total da proposta em destaque */}
            <View style={{ 
                flexDirection: 'row', 
                backgroundColor: '#f5f5f5', 
                borderBottomWidth: 0.5, 
                borderBottomColor: '#e0e0e0',
                marginBottom: 10,
                borderLeftWidth: 0.5,
                borderRightWidth: 0.5,
                borderColor: '#e0e0e0'
            }} wrap={false}>
                <View style={{ width: '82.5%', padding: 5, borderRightWidth: 0.5, borderRightColor: '#e0e0e0' }}>
                    <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 8 }}>VALOR TOTAL DA PROPOSTA</Text>
                </View>
                <View style={{ width: '17.5%', padding: 5 }}>
                    <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 8, textAlign: 'right' }}>
                        R$ {data.valorTotal.replace(/^R\$\s?/, '')}
                    </Text>
                </View>
            </View>

            {/* Seções de rodapé agrupadas para evitar quebras estranhas */}
            <View wrap={false}>
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>INFORMAÇÕES COMPLEMENTARES</Text>
                </View>

                {/* Info Complementar - Layout Plano */}
                <View style={{ borderTopWidth: 0.5, borderColor: '#e0e0e0', marginTop: 4 }}>
                    <View style={{ flexDirection: 'row', borderBottomWidth: 0.5, borderColor: '#e0e0e0', borderLeftWidth: 0.5, borderRightWidth: 0.5 }}>
                        <View style={{ width: '30%', backgroundColor: '#f5f5f5', padding: 5, borderRightWidth: 0.5, borderRightColor: '#e0e0e0' }}>
                            <Text style={{ fontFamily: 'Helvetica-Bold' }}>VALIDADE DA PROPOSTA</Text>
                        </View>
                        <View style={{ width: '70%', padding: 5 }}>
                            <Text>{data.validadeProposta}</Text>
                        </View>
                    </View>
                    <View style={{ flexDirection: 'row', borderBottomWidth: 0.5, borderColor: '#e0e0e0', borderLeftWidth: 0.5, borderRightWidth: 0.5 }}>
                        <View style={{ width: '30%', backgroundColor: '#f5f5f5', padding: 5, borderRightWidth: 0.5, borderRightColor: '#e0e0e0' }}>
                            <Text style={{ fontFamily: 'Helvetica-Bold' }}>PRAZO DE ENTREGA</Text>
                        </View>
                        <View style={{ width: '70%', padding: 5 }}>
                            <Text>{data.prazoEntrega}</Text>
                        </View>
                    </View>
                    <View style={{ flexDirection: 'row', borderBottomWidth: 0.5, borderColor: '#e0e0e0', borderLeftWidth: 0.5, borderRightWidth: 0.5 }}>
                        <View style={{ width: '30%', backgroundColor: '#f5f5f5', padding: 5, borderRightWidth: 0.5, borderRightColor: '#e0e0e0' }}>
                            <Text style={{ fontFamily: 'Helvetica-Bold' }}>DADOS BANCÁRIOS</Text>
                        </View>
                        <View style={{ width: '70%', padding: 5 }}>
                            <Text>{data.dadosBancarios}</Text>
                        </View>
                    </View>
                </View>

                <View style={styles.legalText}>
                    <Text>
                        O preço proposto acima contempla todas as despesas necessárias ao pleno cumprimento do objeto, tais como os encargos (obrigações sociais, impostos, taxas etc.). Declaramos que conhecemos e concordamos com todos os termos do pregão em epígrafe e Cumpre Plenamente Todos os Requisitos de Habilitação exigidos pelo Termo de Referência.
                    </Text>
                </View>

                {/* Assinatura */}
                <View style={styles.signatureArea}>
                    <View style={styles.signatureLine} />
                    <Text style={styles.signatureText}>{data.assinatura}, {data.dataEmissao}</Text>
                </View>
            </View>

            {/* Rodapé */}
            <View style={styles.footer}>
                <Text>{data.empresa} - CNPJ: {data.cnpj} • {data.pregao}</Text>
            </View>
        </Page>
    </Document>
);